import React from 'react'
import i18n from 'config/i18n'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { SubmissionState, SUBMISSION_VIEW_ENUM, PartiposterProfile } from 'state/submissions'
import { EngagementId } from 'state/tasks'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { CommentCell } from 'components/Shared/MuiTable/Cell/CommentCell'
import { MediaCell, MediaCellProps } from 'components/Shared/MuiTable/Cell/MediaCell'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { Country, PLATFORM_ENUM, STEP_TYPE_ENUM } from 'services/api/constants'
import { buildEngagementFieldColDef, sortEngagementType } from '../customTaskEngagementFieldsConfig'
import { parseBackendDate } from 'services/dates'
import { TagContentType } from 'particles/Tag'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { GridColDefProps } from '../SimpleTable/constants'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'

export interface RowsProps {
    // Note: Row id is treated from MUI DataGridPro component as list key, don't use index for dynamic list key
    // issue related: https://gitlab.com/partipost-dev/engineering/backlog-items/-/issues/246
    id: string
    uuid: string
    igUsername?: {
        account: string | null
        socialDomain: string
    }
    tiktokUsername?: {
        account: string | null
        socialDomain: string
    }
    fbUsername?: {
        account: string | null
        socialDomain: string
    }
    twitterUsername?: {
        account: string | null
        socialDomain: string
    }
    view_count_screenshot_files?: string[]
    insights_screenshot_files: string[]
    follower_count: {
        value: string
        color?: string
    }
    engagement_impression_count?: {
        value: string
        color?: string
    }
    engagement_like_count?: {
        value: string
        color?: string
    }
    engagement_reach_count?: {
        value: string
        color?: string
    }
    engagement_view_count?: {
        value: string
        color?: string
    }
    engagement_comment_count?: {
        value: string
        color?: string
    }
    engagement_tag_count?: {
        value: string
        color?: string
    }
    engagement_share_count?: {
        value: string
        color?: string
    }
    engagement_cta_click_count?: {
        value: string
        color?: string
    }
    engagement_save_count?: {
        value: string
        color?: string
    }
    // IG Story Only
    engagement_sticker_tap_count?: {
        value: string
        color?: string
    }
    engagement_link_click_count?: {
        value: string
        color?: string
    }
    // Twitter Only
    engagement_reply_count?: {
        value: string
        color?: string
    }
    engagement_retweet_count?: {
        value: string
        color?: string
    }
    engagement_engagement_count?: {
        value: string
        color?: string
    }
    editsRequested: {
        submission?: SubmissionState
        contentType: TagContentType
    }
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
    lastReviewedBy: {
        value?: string
    }
    lastReviewedAt: {
        value?: string
    }
}

export const getColumns = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    engagementTypes: EngagementId[],
    stepType: STEP_TYPE_ENUM,
    searchablePlatforms: PLATFORM_ENUM[],
    canManageTag: boolean,
    updateViewedRow?: (id: string) => void,
): GridColDefProps[] => {
    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return searchablePlatforms.includes(platform)
    }

    if (viewType === 'edits_requested') {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams): React.ReactElement => {
                    const handleUpdateViewed = () => {
                        if (updateViewedRow) {
                            updateViewedRow(props.row.uuid)
                        }
                    }
                    return <SocialMediaInfoCell {...props} onClick={handleUpdateViewed} />
                },
                subFields: [
                    ...(hasPlatform(PLATFORM_ENUM.IG)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_FOLLOWERS,
                                  headerName: i18n.t('submission.table.instagramFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: i18n.t('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.FB)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_FRIENDS,
                                  headerName: i18n.t('submission.table.facebookFriendsLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: i18n.t('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                                  headerName: i18n.t('submission.table.tikTokFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                        ? [
                              {
                                  field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                                  headerName: i18n.t('submission.table.twitterFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TWITTER_USERNAME,
                                  headerName: i18n.t('submission.table.twitterUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },
            ...(stepType === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT
                ? [
                      {
                          field: 'view_count_screenshot_files',
                          headerName: i18n.t('submission.table.storyScreenshotLabel'),
                          width: 175,
                          renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            {
                field: 'insights_screenshot_files',
                headerName: i18n.t('submission.table.engagementScreenshotLabel'),
                width: 220,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'editsRequested',
                type: 'string',
                headerName: i18n.t('submission.table.reasonEditRequestLabel'),
                width: 200,
                renderCell: (props: GridRenderCellParams) => <CommentCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'follower_count',
                headerName: i18n.t('submission.table.followerCountLabel'),
                width: 240,
                sortable: false,
                filterable: false,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                renderHeader: getInfoTooltipHeaderCell({
                    width: '190px',
                    title: i18n.t('submission.table.followerCountLabel'),
                    description: i18n.t(
                        'submission.table.followerCountTooltip',
                        'The number Partiposters entered when submitting engagement result for the campaign',
                    ),
                }),
            },
            ...(sortEngagementType(stepType, engagementTypes)
                .map(buildEngagementFieldColDef)
                .filter(item => item !== null) as GridColDef[]),
            {
                field: 'lastReviewedBy',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'lastReviewedAt',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 200,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    } else {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams): React.ReactElement => {
                    const handleUpdateViewed = () => {
                        if (updateViewedRow) {
                            updateViewedRow(props.row.uuid)
                        }
                    }
                    return <SocialMediaInfoCell {...props} onClick={handleUpdateViewed} />
                },
                subFields: [
                    ...(hasPlatform(PLATFORM_ENUM.IG)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_FOLLOWERS,
                                  headerName: i18n.t('submission.table.instagramFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: i18n.t('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.FB)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_FRIENDS,
                                  headerName: i18n.t('submission.table.facebookFriendsLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: i18n.t('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                                  headerName: i18n.t('submission.table.tikTokFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                        ? [
                              {
                                  field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                                  headerName: i18n.t('submission.table.twitterFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TWITTER_USERNAME,
                                  headerName: i18n.t('submission.table.twitterUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },
            ...(stepType === STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT
                ? [
                      {
                          field: 'view_count_screenshot_files',
                          headerName: i18n.t('submission.table.storyScreenshotLabel'),
                          width: 175,
                          renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            {
                field: 'insights_screenshot_files',
                headerName: i18n.t('submission.table.engagementScreenshotLabel'),
                width: 220,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'follower_count',
                headerName: i18n.t('submission.table.followerCountLabel'),
                width: 240,
                sortable: false,
                filterable: false,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                renderHeader: getInfoTooltipHeaderCell({
                    width: '190px',
                    title: i18n.t('submission.table.followerCountLabel'),
                    description: i18n.t(
                        'submission.table.followerCountTooltip',
                        'The number Partiposters entered when submitting engagement result for the campaign',
                    ),
                }),
            },
            ...(sortEngagementType(stepType, engagementTypes)
                .map(buildEngagementFieldColDef)
                .filter(item => item !== null) as GridColDef[]),
            ...(viewType !== SUBMISSION_VIEW_ENUM.PENDING
                ? [
                      {
                          field: 'lastReviewedBy',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                      {
                          field: 'lastReviewedAt',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 160,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    }
}

export const getRows = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    country: Country,
): RowsProps[] => {
    return submissions.map<RowsProps>((submission: SubmissionState) => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.IG)
        const tiktokProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TIKTOK)
        const fbProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.FB)
        const twitterProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TWITTER)
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: {
                profile,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile],
            },
            view_count_screenshot_files:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? submission && submission.data && submission.data.view_count_screenshot_files
                    : submission && submission.reviewed_data && submission.reviewed_data.view_count_screenshot_files,
            insights_screenshot_files:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? submission && submission.data && submission.data.insights_screenshot_files
                    : submission && submission.reviewed_data && submission.reviewed_data.insights_screenshot_files,
            follower_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.follower_count }
                    : { value: submission && submission.reviewed_data && submission.reviewed_data.follower_count },
            engagement_impression_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_impression_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_impression_count,
                      },
            engagement_like_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_like_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_like_count,
                      },
            engagement_reach_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_reach_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_reach_count,
                      },
            engagement_view_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_view_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_view_count,
                      },
            engagement_comment_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_comment_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_comment_count,
                      },
            engagement_tag_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_tag_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_tag_count,
                      },
            engagement_share_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_share_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_share_count,
                      },
            // Tiktok Post only
            engagement_cta_click_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_cta_click_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_cta_click_count,
                      },
            // Tiktok Post and Ig Post only
            engagement_save_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_save_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_save_count,
                      },
            // IG Story Only
            engagement_sticker_tap_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_sticker_tap_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_sticker_tap_count,
                      },
            engagement_link_click_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_link_click_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_link_click_count,
                      },
            // Twitter Only
            engagement_reply_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_reply_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_reply_count,
                      },
            engagement_retweet_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_retweet_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_retweet_count,
                      },
            engagement_engagement_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_engagement_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_engagement_count,
                      },
            editsRequested:
                viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { submission: submission, contentType: 'warning' }
                    : { submission: undefined, contentType: 'warning' },

            lastReviewedBy: {
                value: submission.reviewed_by_system
                    ? i18n.t('statusLabel.autoApproved')
                    : submission.moved_out_of_pending_by?.name,
            },
            lastReviewedAt: {
                value: submission.moved_out_of_pending_at
                    ? parseBackendDate(submission.moved_out_of_pending_at, country, { showTime: true })
                    : undefined,
            },
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
        }
    })
}
